import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as additionalQuestionsSchema } from './business-additional-questions-schema.js';
import { F as FormController } from './form.js';
import { A as AdditionalQuestions } from './Business.js';
import { B as BusinessFormStepV2 } from './business-form-types.js';
import { C as CURRENCY_MASK } from './form-input-masks.js';
import { b as businessServiceReceivedOptions, r as recurringPaymentsOptions, s as seasonalBusinessOptions } from './business-form-options.js';
import { d as defineCustomElement$7 } from './custom-popper2.js';
import { d as defineCustomElement$6 } from './form-control-error-text2.js';
import { d as defineCustomElement$5 } from './form-control-help-text2.js';
import { d as defineCustomElement$4 } from './form-control-monetary2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const AdditionalQuestionsFormStepCore = /*@__PURE__*/ proxyCustomElement(class AdditionalQuestionsFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    this.additional_questions = new AdditionalQuestions(response.data.additional_questions || {});
                    this.formController.setInitialValues(Object.assign({}, this.additional_questions));
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.formLoading.emit(false)
            });
        };
        this.sendData = (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: this.patchPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: { submittedData } });
                    this.stepCompleted.emit({ data: submittedData, formStep: BusinessFormStepV2.additionalQuestions });
                    this.formLoading.emit(false);
                }
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
            this.additional_questions = new AdditionalQuestions(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.additional_questions = {};
        this.recurringPayments = false;
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    recurringPaymentsWatcher(newValue) {
        if ((newValue === null || newValue === void 0 ? void 0 : newValue.business_recurring_payments) === 'Yes') {
            this.recurringPayments = true;
        }
        else {
            this.recurringPayments = false;
        }
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    get patchPayload() {
        let formValues = new AdditionalQuestions(this.formController.values.getValue()).payload;
        return { additional_questions: formValues };
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(additionalQuestionsSchema(this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g;
        const additionalQuestionsDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: '469cda15cbb05da33e26bc645cc51ebbbdf6e7fc' }, h("fieldset", { key: 'b00875b7d3f3d3ad338c38000cc29b3a550e8a50' }, h("div", { key: '6dfc3d60d5b5469613681715bf1965752fdb9662', class: "d-flex align-items-center gap-2" }, h("legend", { key: '65b2a3769f96714563a468eca3f1d7e82d6a1538', class: "mb-0" }, "Additional Questions"), h("form-control-tooltip", { key: '7a51538fe74cf8e0f3ed49042d6301fb2bd7909b', helpText: "This information helps us understand the business and identify and monitor trends to safeguard you and your customers." })), h("hr", { key: '42496d5e78ed4922a0165caab9e6e7e48cb44616', class: "mt-2" }), h("div", { key: '0d7ff24d81a3f54e223293c393d4ce89588a1c58', class: "row gy-3" }, h("div", { key: '12a1c72e5e5802f7729f2a09764a5cffa194639d', class: "col-12 col-md-6" }, h("form-control-monetary", { key: 'c9f4aecc5b7108c788237f776953c5cd9ae844b8', name: "business_revenue", label: "What is your business\u2019 estimated annual revenue from its primary business activities?", inputHandler: this.inputHandler, errorText: (_a = this.errors) === null || _a === void 0 ? void 0 : _a.business_revenue, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_revenue, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: 'ecd78939d2a7ac81932898f65f3c9424f157fb3d', class: "col-12 col-md-6" }, h("form-control-monetary", { key: '5df34b238082a11a4589a05e24c53650bd2c0b79', name: "business_payment_volume", label: "What is your business' annual credit card & ACH volume anticipated to process?", inputHandler: this.inputHandler, errorText: (_b = this.errors) === null || _b === void 0 ? void 0 : _b.business_payment_volume, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_payment_volume, maskOptions: CURRENCY_MASK.WHOLE })), h("div", { key: '9db14123245c84eb3dc33d28154ac9be458987cc', class: "col-12" }, h("form-control-select", { key: '7e9899a2e41c14ae968c5451e46d445cbaa289a4', name: "business_when_service_received", label: "On average, how long after paying will your customers typically receive their goods or services?", inputHandler: this.inputHandler, errorText: (_c = this.errors) === null || _c === void 0 ? void 0 : _c.business_when_service_received, options: businessServiceReceivedOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_when_service_received })), h("div", { key: 'f5ebffe9294cbe7e16487c8e63009f7c4901fee1', class: "col-12" }, h("form-control-select", { key: '32a0f978252a52cff90a3a9c1e675515960ee016', name: "business_recurring_payments", label: "Does your business offer recurring payments?", inputHandler: this.inputHandler, errorText: (_d = this.errors) === null || _d === void 0 ? void 0 : _d.business_recurring_payments, options: recurringPaymentsOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments })), h("div", { key: '0aa20a223c15959c7b1e36b8570c17c11c193a8c', class: 'col-12', hidden: !this.recurringPayments }, h("form-control-text", { key: 'e6abbe38ef92091d87c04f832c860ac1a34e855f', name: "business_recurring_payments_percentage", label: "What percent of revenue is generated from each recurring payment type offered?", inputHandler: this.inputHandler, errorText: (_e = this.errors) === null || _e === void 0 ? void 0 : _e.business_recurring_payments_percentage, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_recurring_payments_percentage, helpText: "For example: 50% monthly, 50% annual." })), h("div", { key: '1c2fbe366315babd398e48b38861cb2b8336b287', class: "col-12" }, h("form-control-select", { key: 'eb45c69781ea51ab35f3f0782d1245f553ff63a5', name: "business_seasonal", label: "Is this business seasonal?", inputHandler: this.inputHandler, errorText: (_f = this.errors) === null || _f === void 0 ? void 0 : _f.business_seasonal, options: seasonalBusinessOptions, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_seasonal })), h("div", { key: 'ded80ce1cf8b0fd1b580d5002a1a869aed132f62', class: "col-12" }, h("form-control-text", { key: '80612b8d9381072e0ae670f07b649f01f1cfed66', name: "business_other_payment_details", label: "Is there anything else you would like us to know about how your customers pay the business? (optional)", inputHandler: this.inputHandler, errorText: (_g = this.errors) === null || _g === void 0 ? void 0 : _g.business_other_payment_details, defaultValue: additionalQuestionsDefaultValue === null || additionalQuestionsDefaultValue === void 0 ? void 0 : additionalQuestionsDefaultValue.business_other_payment_details }))))));
    }
    static get watchers() { return {
        "additional_questions": ["recurringPaymentsWatcher"]
    }; }
}, [0, "justifi-additional-questions-form-step-core", {
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "additional_questions": [32],
        "recurringPayments": [32],
        "validateAndSubmit": [64]
    }, undefined, {
        "additional_questions": ["recurringPaymentsWatcher"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-additional-questions-form-step-core", "custom-popper", "form-control-error-text", "form-control-help-text", "form-control-monetary", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-additional-questions-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, AdditionalQuestionsFormStepCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-monetary":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { AdditionalQuestionsFormStepCore as A, defineCustomElement as d };
